import React, {
  forwardRef,
  PropsWithChildren,
  useRef,
  useImperativeHandle,
  ReactElement,
} from "react";
import {
  useToggle,
  useWindowSize,
  useLocalStorage,
  useResizeObserver,
} from "usehooks-ts";
import { useAuth } from "shared-auth";
import Footer from "../Footer";
import Header from "../Header";
import LeftHandNav from "../LeftHandNav";
import NavScreenLocker from "../NavScreenLocker";
import UserMenu from "../UserMenu";
import { breakpoints } from "../../../../../patternlib-styles/settings/breakpoints";
import {
  contentContainerId,
  lhnLocalStorageVisibilityKey,
} from "../../constants";
import ThemeContext, { ThemeContextValues } from "../NavThemeContext";

export interface GenericAppShellProps {
  leftHandNavItems?: React.ReactNode;
  userMenuItems?: React.ReactNode;
  theme?: string;
  dynamicHeaderContent?: React.ReactNode;
  footerComponent?: React.ReactNode;
  hideFooter?: boolean;
  hideHeader?: boolean;
  avatar?: string;
  serviceBannerPortalNone?: ReactElement;
}

export interface GenericAppShellRef {
  toggleLhn: () => void;
}

const GenericAppShell = forwardRef<
  GenericAppShellRef,
  PropsWithChildren<GenericAppShellProps>
>(
  (
    {
      leftHandNavItems,
      userMenuItems,
      theme = ThemeContextValues.Default,
      dynamicHeaderContent,
      footerComponent,
      hideFooter,
      avatar,
      serviceBannerPortalNone,
      children,
    },
    ref
  ) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const { height: headerHeight } = useResizeObserver({
      ref: headerRef,
    });
    const auth = useAuth();
    const userMenuButtonRef = useRef(null);
    const { width } = useWindowSize();
    const isMediumOrLess = width < breakpoints.mediumDeviceMaxWidth;
    const isMobile = width < breakpoints.mediumDeviceMinWidth;

    const [lhnLocalStorage, setLhnLocalStorage] = useLocalStorage(
      lhnLocalStorageVisibilityKey,
      false
    );

    const [lhnIsVisible, toggleLhnState] = useToggle(
      isMobile ? false : lhnLocalStorage
    );
    const [userMenuVisible, toggleUserMenu] = useToggle(false);

    const toggleLhn = () => {
      toggleLhnState();
      setLhnLocalStorage((prev) => !prev);
    };

    useImperativeHandle(ref, () => ({
      toggleLhn,
    }));

    return (
      <ThemeContext.Provider value={theme}>
        <Header
          isLhnVisible={lhnIsVisible}
          toggleLhn={toggleLhn}
          toggleUserMenu={toggleUserMenu}
          userMenuButtonRef={userMenuButtonRef}
          dynamicHeaderContent={dynamicHeaderContent}
          avatar={avatar}
          serviceBannerPortalNone={serviceBannerPortalNone}
          ref={headerRef}
        />
        <div className="flex">
          {auth.isAuthenticated && (
            <LeftHandNav visible={lhnIsVisible} headerHeight={headerHeight}>
              {leftHandNavItems}
            </LeftHandNav>
          )}
          <div
            id={contentContainerId}
            className="flex min-h-[calc(100vh_-_60px)] flex-1 flex-col"
          >
            {lhnIsVisible && isMediumOrLess && <NavScreenLocker />}
            {children}
            {!hideFooter && (footerComponent || <Footer />)}
          </div>
          {auth.isAuthenticated && (
            <UserMenu
              visible={userMenuVisible}
              userMenuButtonRef={userMenuButtonRef}
              toggle={toggleUserMenu}
              avatar={avatar}
            >
              {userMenuItems}
            </UserMenu>
          )}
        </div>
      </ThemeContext.Provider>
    );
  }
);

export default GenericAppShell;
